import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, GoldButton, GhostButton, StatBadge } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Scale, ExternalLink } from "lucide-react";

const PAGES = [
  { key: "terms", label: "Terms & Conditions", public_path: "termos" },
  { key: "privacy", label: "Privacy Policy", public_path: "privacidade" },
  { key: "cookies", label: "Cookie Policy", public_path: "cookies" },
  { key: "complaints", label: "Livro de Reclamações", public_path: "reclamacoes" },
];

export default function LegalPages() {
  const [pages, setPages] = useState([]);
  const load = () => api.get("/admin/settings/legal").then((r) => setPages(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="p-10 max-w-[1200px]">
      <PageHeader eyebrow="Settings · Compliance" title="Legal pages" description="Terms & Conditions, Privacy Policy and Cookie Policy — versioned per language, publishable to the public site." />

      <Tabs defaultValue="terms">
        <TabsList className="bg-transparent border border-slate-200 rounded-none h-auto p-0">
          {PAGES.map((p) => (
            <TabsTrigger key={p.key} value={p.key} data-testid={`legal-tab-${p.key}`}
              className="px-6 py-3 text-xs uppercase tracking-wide rounded-none data-[state=active]:bg-[#f59e0b] data-[state=active]:text-[#0a0f1e]">
              {p.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {PAGES.map((p) => (
          <TabsContent key={p.key} value={p.key} className="mt-6">
            <PageEditor
              pageDef={p}
              pt={pages.find(x => x.type === p.key && x.lang === "PT")}
              en={pages.find(x => x.type === p.key && x.lang === "EN")}
              onSaved={load}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function PageEditor({ pageDef, pt, en, onSaved }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <LangEditor pageType={pageDef.key} lang="PT" initial={pt} onSaved={onSaved} publicPath={pageDef.public_path} />
      <LangEditor pageType={pageDef.key} lang="EN" initial={en} onSaved={onSaved} publicPath={pageDef.public_path} />
    </div>
  );
}

function LangEditor({ pageType, lang, initial, onSaved, publicPath }) {
  const [content, setContent] = useState("");
  const [version, setVersion] = useState("1.0.0");
  const [effective, setEffective] = useState("");
  const [published, setPublished] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setContent(initial?.content || "");
    setVersion(initial?.version || "1.0.0");
    setEffective(initial?.effective_date || new Date().toISOString().slice(0, 10));
    setPublished(!!initial?.published);
  }, [initial]);

  const save = async (publishFlag) => {
    setSaving(true);
    try {
      await api.put(`/admin/settings/legal/${pageType}/${lang}`, {
        content, version, effective_date: effective, published: publishFlag,
      });
      toast.success(publishFlag ? `${lang} published` : `${lang} saved as draft`);
      setPublished(!!publishFlag);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-slate-200 bg-white p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border border-[#f59e0b] flex items-center justify-center">
            <Scale className="w-4 h-4 text-[#f59e0b]" />
          </div>
          <div>
            <p className="label-caps text-[#f59e0b]">{lang}</p>
            <div className="text-sm text-[#0a0f1e]">Version {version}</div>
          </div>
        </div>
        <StatBadge status={published ? "active" : "pending"} />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="label-caps block mb-2">Version</label>
          <Input value={version} onChange={(e) => setVersion(e.target.value)} className="input-dt" data-testid={`legal-${pageType}-${lang}-version`} />
        </div>
        <div>
          <label className="label-caps block mb-2">Effective date</label>
          <Input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} className="input-dt" />
        </div>
      </div>

      <label className="label-caps block mb-2">Content (Markdown supported)</label>
      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={16}
        className="input-dt font-mono text-xs"
        data-testid={`legal-${pageType}-${lang}-content`}
        placeholder={`# ${pageType.toUpperCase()} in ${lang}\n\nWrite your legal text here…`}
      />

      <div className="mt-4 flex items-center gap-3">
        <GhostButton onClick={() => save(false)} disabled={saving} data-testid={`legal-save-${pageType}-${lang}`}>Save draft</GhostButton>
        <GoldButton onClick={() => save(true)} disabled={saving} data-testid={`legal-publish-${pageType}-${lang}`}>Publish</GoldButton>
        {published && (
          <a href={`/legal/${publicPath}?lang=${lang}`} target="_blank" rel="noreferrer" className="ml-auto text-xs text-[#f59e0b] hover:underline flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Preview
          </a>
        )}
      </div>
    </div>
  );
}
